import React, { useCallback, useEffect, useMemo, useState } from "react";
import { geofenceAlertsApi, usersApi, productsApi } from "../../services/api.service";
import { T, SectionLabel, Field, Input, Select, Btn } from "../shared/UI";
import { onlyOnAgents } from "../../utils/normalizers";

// Admin view for idle-in-place alerts.
// An alert is raised when an agent stays in the same place for 30+ minutes,
// then repeats every 10 minutes. Each row shows "At Home" when the idle spot is
// within 200 m of the agent's home, otherwise the reported address.
// Formats idle minutes for display. Under an hour it stays as "45m"; once it
// reaches an hour or more it reads as hours and minutes, e.g. "1h 15m" or "2h".
function formatIdle(minutes) {
  if (minutes == null) return "—";
  const mins = Math.round(Number(minutes));
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export default function GeofenceAlertsView() {
  const today = new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({
    agentId: "",
    agentName: "",
    productId: "",
    fromDate: today,
    toDate: today,
  });

  const agentOptions = useMemo(
    () => agents.map(a => ({ id: a.id, name: a.fullName || a.name || a.mobile })),
    [agents]
  );
  const productOptions = useMemo(
    () => products.map(p => ({ id: p.id, name: p.name })),
    [products]
  );

  const loadAgents = useCallback(async () => {
    try {
      const res = await usersApi.agentsMeta();
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      setAgents(onlyOnAgents(raw));
    } catch {
      setAgents([]);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const res = await productsApi.list();
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      setProducts(raw);
    } catch {
      setProducts([]);
    }
  }, []);

  const loadAlerts = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: meta.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== "" && v != null)
        ),
      };
      const res = await geofenceAlertsApi.list(params);
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      const metaRes = res.data?.meta || { page, limit: meta.limit, total: raw.length, totalPages: 1 };
      setRows(raw);
      setMeta({
        page: metaRes.page || page,
        limit: metaRes.limit || meta.limit,
        total: metaRes.total || raw.length,
        totalPages: metaRes.totalPages || 1,
      });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load geofence alerts";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, meta.limit]);

  const mapLink = (lat, lng) =>
    lat != null && lng != null ? `https://www.google.com/maps?q=${lat},${lng}` : null;

  // Exports every row matching the current filters — the server streams the full
  // filtered dataset as a CSV file (not just the page shown on screen).
  const exportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v != null)
      );
      const res = await geofenceAlertsApi.exportCsv(activeFilters);
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `idle-alerts-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Export failed";
      setError(msg);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { loadAgents(); }, [loadAgents]);
  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadAlerts(1); }, [loadAlerts]);

  return (
    <div style={styles.wrap}>
      <div style={styles.toolbar}>
        <SectionLabel>IDLE ALERTS</SectionLabel>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Btn variant="ghost" onClick={() => loadAlerts(1)}>Apply</Btn>
          <Btn variant="outline" onClick={exportCsv} disabled={!rows.length || exporting}>
            {exporting ? "Exporting…" : "Export CSV"}
          </Btn>
        </div>
      </div>

      <div style={styles.hint}>
        Raised when an agent stays in the same place for 30+ minutes, then every 10 minutes. Shows “At Home” when the spot is within 200 m of the agent’s home, otherwise the address.
      </div>

      <div style={styles.filters}>
        <Field label="AGENT">
          <Select value={filters.agentId} onChange={e => setFilters(f => ({ ...f, agentId: e.target.value }))}>
            <option value="">All</option>
            {agentOptions.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
        <Field label="AGENT NAME">
          <Input value={filters.agentName} onChange={e => setFilters(f => ({ ...f, agentName: e.target.value }))} />
        </Field>
        <Field label="PRODUCT">
          <Select value={filters.productId} onChange={e => setFilters(f => ({ ...f, productId: e.target.value }))}>
            <option value="">All</option>
            {productOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
        <Field label="FROM DATE">
          <Input type="date" value={filters.fromDate} onChange={e => setFilters(f => ({ ...f, fromDate: e.target.value }))} />
        </Field>
        <Field label="TO DATE">
          <Input type="date" value={filters.toDate} onChange={e => setFilters(f => ({ ...f, toDate: e.target.value }))} />
        </Field>
      </div>

      {loading && <div style={styles.info}>Loading alerts…</div>}
      {error && <div style={{ ...styles.info, color: T.sos }}>{error}</div>}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {["Agent","Product","Idle Since","Idle","Where","Distance","Alert #","Location","Triggered At"].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const loc = mapLink(r.latitude, r.longitude);
              const isHome = Number(r.isHome) === 1;
              return (
                <tr key={r.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={{ fontWeight: 600 }}>{r.agentName || r.agentId}</div>
                  </td>
                  <td style={styles.tdM}>{r.productName || "—"}</td>
                  <td style={styles.tdM}>{r.idleSince}</td>
                  <td style={styles.tdM}>{formatIdle(r.idleMinutes)}</td>
                  <td style={styles.td}>
                    {isHome
                      ? <span style={styles.homeTag}>🏠 At Home</span>
                      : <span title={r.address || ""}>{r.address || "Away"}</span>}
                  </td>
                  <td style={styles.tdM}>{r.distanceM != null ? `${Math.round(r.distanceM)}m` : "—"}</td>
                  <td style={styles.tdM}>#{r.alertNumber}</td>
                  <td style={styles.tdM}>
                    {loc ? <a href={loc} target="_blank" rel="noreferrer" style={styles.link}>View</a> : "—"}
                  </td>
                  <td style={styles.tdM}>{r.triggeredAt}</td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td style={styles.empty} colSpan={9}>No idle alerts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.pagination}>
        <Btn size="sm" variant="ghost" disabled={meta.page <= 1} onClick={() => loadAlerts(meta.page - 1)}>Prev</Btn>
        <span style={styles.pageLabel}>Page {meta.page} of {meta.totalPages}</span>
        <Btn size="sm" variant="ghost" disabled={meta.page >= meta.totalPages} onClick={() => loadAlerts(meta.page + 1)}>Next</Btn>
      </div>
    </div>
  );
}

const styles = {
  wrap: { flex:1, overflowY:"auto", padding:20, display:"flex", flexDirection:"column", gap:16 },
  toolbar: { display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" },
  hint: { fontSize:11, color:T.muted, marginTop:-6 },
  filters: { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px,1fr))", gap:12, background:T.s2, border:`1px solid ${T.border}`, borderRadius:12, padding:12 },
  info: { fontSize:12, color:T.muted, padding:"6px 4px" },
  tableWrap: { background:T.s2, border:`1px solid ${T.border}`, borderRadius:12, padding:12, overflowX:"auto" },
  table: { width:"100%", borderCollapse:"collapse" },
  th: { textAlign:"left", fontFamily:T.fontMono, fontSize:9, letterSpacing:"1.5px", color:T.muted, padding:"6px 10px", borderBottom:`1px solid ${T.border}`, textTransform:"uppercase", whiteSpace:"nowrap" },
  tr: { borderBottom:`1px solid ${T.border}` },
  td: { padding:"8px 10px", fontSize:12, color:T.text, verticalAlign:"middle", whiteSpace:"nowrap" },
  tdM: { padding:"8px 10px", fontSize:11, color:T.muted, verticalAlign:"middle", whiteSpace:"nowrap" },
  link: { color:T.acc, textDecoration:"none" },
  homeTag: { display:"inline-block", padding:"2px 8px", borderRadius:6, fontSize:11, fontWeight:600, color:T.acc, background:"rgba(124,106,247,0.12)", border:`1px solid ${T.border}`, whiteSpace:"nowrap" },
  empty: { padding:"16px 10px", fontSize:12, color:T.muted, textAlign:"center" },
  pagination: { display:"flex", alignItems:"center", justifyContent:"space-between" },
  pageLabel: { fontSize:11, color:T.muted, fontFamily:T.fontMono },
};
